import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useScopedQueryKey } from '../hooks/useScopedQueryKey';
import { api, apiEndpoints } from '../utils/apiService';

const POLL_MS = 45_000;

/** Both inboxes report their size in `count`; one row is enough to read it. */
async function fetchCount(url: string): Promise<number> {
  const response = await api.get(url);
  if (!response?.ok) return 0;
  const data = (await response.json().catch(() => null)) as {
    count?: number;
  } | null;
  return typeof data?.count === 'number' ? data.count : 0;
}

/**
 * Unread counts for the navigation badges. Polled rather than pushed, and
 * never in a hidden tab — the number is a hint, not a live feed. The
 * voicemail key is the one the Voicemail page invalidates, so reading a
 * message there clears the badge without a second request.
 */
export function useNavCounts({
  sms = false,
  voicemail = false,
  events = false,
} = {}): {
  sms: number;
  voicemail: number;
  events: number;
} {
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const smsKey = useScopedQueryKey('nav-unread-sms');
  const voicemailKey = useScopedQueryKey('voicemail-unread-count');
  const eventsKey = useScopedQueryKey('nav-unseen-events');

  const smsCount = useQuery({
    queryKey: smsKey,
    queryFn: () =>
      fetchCount(
        apiEndpoints.smsConversations({
          unread: true,
          archived: false,
          page: 1,
          page_size: 1,
        }),
      ),
    enabled: signedIn && sms,
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    retry: false,
  }).data;

  const voicemailCount = useQuery({
    queryKey: voicemailKey,
    queryFn: () =>
      fetchCount(
        apiEndpoints.voicemails({
          folder: 'inbox',
          unread: true,
          page: 1,
          page_size: 1,
        }),
      ),
    enabled: signedIn && voicemail,
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    retry: false,
  }).data;

  const eventsCount = useQuery({
    queryKey: eventsKey,
    queryFn: () =>
      fetchCount(
        apiEndpoints.getEventInquiries({
          dashboard_state: 'unseen',
          page: 1,
          page_size: 1,
        }),
      ),
    enabled: signedIn && events,
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    retry: false,
  }).data;

  return {
    sms: smsCount ?? 0,
    voicemail: voicemailCount ?? 0,
    events: eventsCount ?? 0,
  };
}
